/**
 * Rider Status Types
 * 라이더 현황 페이지 관련 타입 정의
 */

// ===========================================
// 1. 라이더 기본 정보 타입
// ===========================================

export type Rider = {
    id: string;
    name: string;
    phoneNumber: string;
    joinDate: string;
    contractStatusName?: string; // 계약상태명
    deliveryMethName?: string; // 배달수단명
    insurCodeName?: string; // 보험종류명
    insurStatusCodeName?: string; // 보험상태명
    preAcctYn?: string; //선정산여부
    supplierName?: string; // 협력사명
    suppId?: string; // 협력사 ID
    contractEndDate?: string; // 계약종료일자
    riderType?: string; // 라이더구분 (GENERAL, BRANCH_MANAGER, 65_YEARS_AGE)
    riderTypeName?: string; // 라이더구분명 (일반, 지점장, 만65세이상)
    balAmt?: number; // 보유잔액
    registNum?: string; // 주민등록번호
    vpayRegistYn?: string;  // Vpay 가입여부 (Y/N)
};

// ===========================================
// 2. API 요청/응답 타입
// ===========================================

export type RiderApiItem = {
    riderId?: string;
    name?: string;
    phoneNumber?: string;
    deliveryStatus?: string;
    deliveryStatusName?: string;
    insDate?: string;
    deliveryMeth?: string;
    deliveryMethName?: string;
    registNum?: string;
    status?: {
        code?: string;
        description?: string;
        contractStatusCode?: string;
        contractStatusName?: string;
        deliveryStatusCode?: string;
    };
    contStatus?: string;
    contractStatusName?: string;
    suppId?: string;
    centerName?: string; // 협력사명 (API 응답에 포함될 수 있음)
    suppName?: string; // 협력사명 (API 응답에 포함될 수 있음)
    insurCode?: string;
    insurCodeName?: string;
    insurStatusCd?: string;
    insurStatusCodeName?: string;
    preAcctYn?: string;
    // 정산 정보
    acctApplyDay?: string; // 현재 정산 적용일
    acctApplyType?: string; // 현재 정산 타입
    acctApplyTypeName?: string; // 현재 정산 타입명
    nextAcctApplyDay?: string; // 다음 정산 적용일
    nextAcctApplyType?: string; // 다음 정산 타입
    nextAcctApplyTypeName?: string; // 다음 정산 타입명
    // 계좌 정보
    accountNum?: string;
    bankCode?: string;
    accName?: string; // 예금주명
    // 기타 정보
    riderType?: string;
    riderTypeName?: string;
    typeCode?: string;
    typeName?: string;
    birthDate?: string;
    insUser?: string;
    pwNum?: string;
    updDate?: string;
    updUser?: string;
    // 날짜 정보
    riderInsDate?: string; // 등록일
    contEdDate?: string; // 계약종료일
    // 잔액 정보
    balAmt?: number; // 보유잔액
    // Vpay 가입여부
    vpayRegistYn?: string;  // Vpay 가입여부 (Y/N)
};

// ===========================================
// 3. 필터 및 기타 타입
// ===========================================

export type RiderFilter = {
    riderName?: string;
    riderId?: string;
    phoneNum?: string;
    centerId?: string;
    contStatus?: string;
};

export type Supplier = {
    centerId: string;
    name: string;
    statusCode?: string;
    statusDesc?: string;
};

// API 응답 아이템 타입 (검색 API용)
export type RiderSearchResponseItem = {
    centerId?: string;
    id?: string;
    center_id?: string;
    name?: string;
    centerName?: string;
    statusCode?: string;
    status?: {
        code?: string;
        desc?: string;
    };
    statusDesc?: string;
    statusDescription?: string;
};

// 협력사 목록 API 응답 타입
/** 협력사 API 응답 아이템 */
export type SupplierApiItem = {
    centerId?: string;
    suppId?: string;
    id?: string;
    name?: string;
    suppName?: string;
    centerName?: string;
};

export type SupplierListApiResponse =
    | RiderSearchResponseItem[]
    | { suppliers: RiderSearchResponseItem[] }
    | { data: RiderSearchResponseItem[] };

// ===========================================
// 4. 계약 상태 상수
// ===========================================

// 라이더 계약 상태
export const CONTRACT_STATUS = {
    UNDER_CONTRACT: 'UNDER_CONTRACT',
    TERMINATED_CONTRACT: 'TERMINATED_CONTRACT',
    CONTRACT_PENDING: 'CONTRACT_PENDING',
} as const;

export type ContractStatus = typeof CONTRACT_STATUS[keyof typeof CONTRACT_STATUS];

// 라이더 계약 상태 라벨 매핑
export const CONTRACT_STATUS_LABELS: Record<ContractStatus, string> = {
    [CONTRACT_STATUS.UNDER_CONTRACT]: '계약중',
    [CONTRACT_STATUS.TERMINATED_CONTRACT]: '계약종료',
    [CONTRACT_STATUS.CONTRACT_PENDING]: '계약대기',
};

// 계약 상태 옵션 (셀렉트박스용)
export const CONTRACT_STATUS_OPTIONS = [
    { value: '', label: '전체' },
    { value: CONTRACT_STATUS.UNDER_CONTRACT, label: CONTRACT_STATUS_LABELS[CONTRACT_STATUS.UNDER_CONTRACT] },
    { value: CONTRACT_STATUS.TERMINATED_CONTRACT, label: CONTRACT_STATUS_LABELS[CONTRACT_STATUS.TERMINATED_CONTRACT] },
] as const;

// 기본 페이지당 아이템 수
export const ITEMS_PER_PAGE = {
    DEFAULT: 10,
    LARGE: 15,
    EXTRA_LARGE: 20,
} as const;

// ===========================================
// 5. 스토리지 키 상수
// ===========================================

export const STORAGE_KEYS = {
    /** 라이더 현황 필터 */
    RIDER_STATUS_FILTERS: 'riderStatusFilters',
} as const;

export type StorageKey = typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS];
